import { escapeHtml } from './escapeHtml';
import { formatSkippedComment } from './formatSkippedComment';
import type { SerializeOptions } from './serializeOptions';
import { formatRowLocationId, formatTableLocationId } from '../../slate-api';
import type {
  CastBlockContent,
  CastPrimaryStyle,
  CastTable,
  CastTableCell,
  CastTableRow,
} from '../types';

type SerializeBlock = (
  block: CastBlockContent,
  options: SerializeOptions,
  sectionStyle?: CastPrimaryStyle
) => string | null;

export function serializeTable(
  table: CastTable,
  options: SerializeOptions,
  serializeBlock: SerializeBlock,
  sectionStyle?: CastPrimaryStyle
): string | null {
  if (table.included !== true) return null;

  const parts: string[] = [];
  let skippedRows = 0;

  const flushSkipped = (): void => {
    if (options.emitSkipped) {
      const comment = formatSkippedComment([{ count: skippedRows, noun: 'table row' }]);
      if (comment !== null) parts.push(comment);
    }
    skippedRows = 0;
  };

  for (const row of table.children) {
    const rendered = serializeRow(row, options, serializeBlock, sectionStyle);
    if (rendered === null) {
      skippedRows += 1;
      continue;
    }
    flushSkipped();
    parts.push(rendered);
  }
  flushSkipped();

  const idAttr = table.id ? formatTableLocationId(table.id) : undefined;
  return wrapContainer('table', parts.join('\n'), idAttr);
}

function serializeRow(
  row: CastTableRow,
  options: SerializeOptions,
  serializeBlock: SerializeBlock,
  sectionStyle: CastPrimaryStyle | undefined
): string | null {
  if (row.included !== true) return null;

  const cells = row.children
    .map(cell => serializeCell(cell, options, serializeBlock, sectionStyle))
    .filter((cell): cell is string => cell !== null);
  const idAttr = row.id ? formatRowLocationId(row.id) : undefined;
  return wrapContainer('tr', cells.join('\n'), idAttr);
}

function serializeCell(
  cell: CastTableCell,
  options: SerializeOptions,
  serializeBlock: SerializeBlock,
  sectionStyle: CastPrimaryStyle | undefined
): string | null {
  if (cell.included !== true) return null;

  /**
   * No skipped-cell comments: the expansion pass always includes a whole row
   * (and its cells) at minimum, so a partially-included row never happens in
   * practice. Excluded cells are just dropped if a caller sets them manually.
   */
  const blocks = cell.children
    .map(block => serializeBlock(block, options, sectionStyle))
    .filter((block): block is string => block !== null);
  return wrapContainer('td', blocks.join('\n'));
}

/**
 * Wraps a table container's inner content on its own lines for readability:
 * `<tag>\n${inner}\n</tag>`. An empty container collapses to `<tag></tag>`.
 * When `id` is given (the prefixed `table-…`/`row-…` anchor) it is emitted as
 * an id attribute; a source without ids (legacy unmigrated docs) omits it.
 */
function wrapContainer(tag: 'table' | 'td' | 'tr', inner: string, id?: string): string {
  const openTag = id === undefined ? `<${tag}>` : `<${tag} id="${escapeHtml(id)}">`;
  return inner === '' ? `${openTag}</${tag}>` : `${openTag}\n${inner}\n</${tag}>`;
}
